import * as React from "react";
import { css } from "@emotion/core";
import {
  CompoundTag,
  Button,
  Card,
  CardList,
  Tag,
  ButtonGroup,
  Icon,
} from "@blueprintjs/core";
import { formatBytes } from "utils/format";
import { useUploadMachineSelector } from "../../context/upload-machine";
import { generateUniqueFileIdentifier } from "../../state/uploadMachine";
import { FileUploadActor } from "../../state/uploadMachine/types";
import type { ProcessedFile, PointCloudData } from "../../state/types";
import { formatInTimeZone } from "date-fns-tz";
import { OrderMachineContext } from "components/Orders/state/orderMachine";
import { LidarMachineContext } from "../../state/lidarMachine";
import { ConfirmRemoveDialog } from "./ConfirmRemoveDialog";
import { usePointCloudDropzone } from "../../hooks";
import { UploadStatusIcon } from "./UploadStatusIcon";

function PointCloudConfirmRemoveDialog() {
  const lidarMachine = LidarMachineContext.useActorRef();

  const show = LidarMachineContext.useSelector((state) =>
    state.matches("remove")
  );

  const pointcloud = LidarMachineContext.useSelector((state) =>
    state.context.uploads.find((u) => u.id === state.context.removing)
  );

  const handleCancel = () => {
    lidarMachine.send({ type: "CANCEL_REMOVE" });
  };

  const handleConfirm = () => {
    lidarMachine.send({ type: "CONFIRM_REMOVE" });
  };

  const timezone = OrderMachineContext.useSelector(
    (state) => state.context.timezone
  );

  const timestamps = pointcloud?.data?.timestamps;
  const points = pointcloud?.data?.points;

  if (show === false) return null;

  return (
    <ConfirmRemoveDialog onCancel={handleCancel} onConfirm={handleConfirm}>
      <Card
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <span>{pointcloud?.file?.name}</span>
      </Card>
      <Card>
        <div
          css={css`
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
          `}
        >
          <span>Size</span>
          <span>{formatBytes(pointcloud.file.size)}</span>
        </div>
      </Card>
      {timestamps && timezone ? (
        <Card>
          <div
            css={css`
              width: 100%;
              display: flex;
              flex-direction: row;
              justify-content: space-between;
            `}
          >
            <span>Capture Date</span>
            <span>
              {`${formatInTimeZone(
                new Date(timestamps[0]),
                timezone,
                "yyyy-MM-dd"
              )} ${formatInTimeZone(
                new Date(timestamps[0]),
                timezone,
                "HH:mm aa"
              )} - ${formatInTimeZone(
                new Date(timestamps[1]),
                timezone,
                "HH:mm aa"
              )} ${formatInTimeZone(new Date(timestamps[1]), timezone, "zzz")}`}
            </span>
          </div>
        </Card>
      ) : null}
      {points ? (
        <Card>
          <div
            css={css`
              width: 100%;
              display: flex;
              flex-direction: row;
              justify-content: space-between;
            `}
          >
            <span>Points</span>
            <span>{points.toLocaleString()}</span>
          </div>
        </Card>
      ) : null}
    </ConfirmRemoveDialog>
  );
}

function PointCloudConfirmRemoveMissingDialog() {
  const lidarMachine = LidarMachineContext.useActorRef();

  const show = LidarMachineContext.useSelector((state) =>
    state.matches({ removeMissing: "confirm" })
  );

  const missingCount = LidarMachineContext.useSelector(
    (state) =>
      state.context.uploads.filter(
        (upload) =>
          !(upload.file instanceof File) &&
          (upload as any).status !== "uploaded"
      ).length
  );

  const handleCancel = () => {
    lidarMachine.send({ type: "CANCEL_REMOVE" });
  };

  const handleConfirm = () => {
    lidarMachine.send({ type: "CONFIRM_REMOVE_MISSING" });
  };

  if (show === false) return null;

  return (
    <ConfirmRemoveDialog onCancel={handleCancel} onConfirm={handleConfirm}>
      <Card
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <span>
          Remove {missingCount} missing point cloud
          {missingCount === 1 ? "" : "s"}?
        </span>
      </Card>
    </ConfirmRemoveDialog>
  );
}

interface PointCloudRowProps {
  upload: ProcessedFile<PointCloudData>;
  onFocus?: (bbox: number[]) => void;
  isOld: boolean;
}

function PointCloudRow(props: PointCloudRowProps) {
  const lidarMachine = LidarMachineContext.useActorRef();
  const { file, progress } = props.upload;

  const timestamps = props.upload.data?.timestamps;
  const points = props.upload.data?.points;

  const handleClick = () => {
    if (props.onFocus && props.upload.data?.bbox) {
      props.onFocus(props.upload.data.bbox);
    }
  };

  const completed = useUploadMachineSelector((state) => {
    const fileIdentifier = generateUniqueFileIdentifier(file);
    return state.context.uploadedFiles.includes(fileIdentifier.name);
  });

  const active = useUploadMachineSelector((state) => {
    const fileIdentifier = generateUniqueFileIdentifier(file);
    return state.context.fileUploadActors.some((actor: FileUploadActor) => {
      return actor.id === `file-${fileIdentifier.name}`;
    });
  });

  const uploadInProgress = useUploadMachineSelector((state) => {
    return state.context.fileUploadActors.length > 0;
  });

  const timezone = OrderMachineContext.useSelector(
    (state) => state.context.timezone
  );

  const idle = LidarMachineContext.useSelector((state) =>
    state.matches("idle")
  );

  const handleRemove = () => {
    lidarMachine.send({ type: "REMOVE_FILE", id: props.upload.id });
  };

  return (
    <div
      css={css`
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: start;
        gap: 0.25rem;
      `}
    >
      <div
        css={css`
          border: 1px solid var(--neutral-6);
        `}
      >
        <UploadStatusIcon
          completed={completed}
          active={active}
          uploadInProgress={uploadInProgress}
          fileAvailable={
            props.upload.file instanceof File ||
            (props.upload as any).status === "uploaded"
          }
          isOld={props.isOld}
          type="pointcloud"
          onClick={handleClick}
        />
      </div>
      <div
        css={css`
          flex-grow: 1;
          border: 1px solid
            ${props.upload.error ? "var(--danger)" : "var(--neutral-5)"};
          background-color: var(--neutral-6-translucent);
          position: relative;
          padding: ${progress === 1
            ? "0.5rem"
            : props.upload.error
            ? "0.25rem 0.5rem 0.25rem 0.5rem"
            : "0.25rem 0.25rem 0.25rem 0.5rem"};
          cursor: ${props.onFocus && props.upload.data?.bbox
            ? "pointer"
            : "default"};
          transition: background-color 0.2s ease;

          &:hover {
            background-color: ${props.onFocus && props.upload.data?.bbox
              ? "var(--neutral-6)"
              : "var(--neutral-6-translucent)"};
          }
        `}
        onClick={
          props.onFocus && props.upload.data?.bbox ? handleClick : undefined
        }
      >
        <div
          css={css`
            position: absolute;
            z-index: 0;
            left: 0;
            top: 0;
            height: 100%;
            width: ${(progress || 0) * 100}%;
            background-color: var(--fybr-blue);
            opacity: ${(progress || 0) < 1 ? 1 : 0};
            transition: opacity 0.3s ease-in-out;
          `}
        />
        <div
          css={css`
            position: relative;
            z-index: 1;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            height: 100%;
          `}
        >
          {props.upload.error ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                `}
              >
                <span>{file.name}</span>
              </div>
              <span>Could not load file: {props.upload.error}</span>
            </div>
          ) : (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 0.5rem;
              `}
            >
              <span>{file.name}</span>
            </div>
          )}
          <div
            css={css`
              display: flex;
              flex-direction: row;
              gap: 0.5rem;
              align-items: center;
            `}
          >
            {props.upload.error ? null : (
              <span>
                {progress < 1 ? `${formatBytes(progress * file.size)}/` : ""}
                {formatBytes(file.size)}
              </span>
            )}
            <Button
              icon="trash"
              intent="danger"
              disabled={idle === false || uploadInProgress}
              onClick={(event) => {
                event.stopPropagation();
                handleRemove();
              }}
            />
          </div>
        </div>
        {timestamps && timezone ? (
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
              gap: 0.5rem;
              transition: opacity 0.3s ease-in-out;
              opacity: ${(progress || 0) < 1 ? 0 : 1};
              margin-top: 0.5rem;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: row;
              `}
            >
              <CompoundTag leftContent="Capture Date:" size="large">
                {`${formatInTimeZone(
                  new Date(timestamps[0]),
                  timezone,
                  "yyyy-MM-dd"
                )} ${formatInTimeZone(
                  new Date(timestamps[0]),
                  timezone,
                  "HH:mm aa"
                )} - ${formatInTimeZone(
                  new Date(timestamps[1]),
                  timezone,
                  "HH:mm aa"
                )} ${formatInTimeZone(
                  new Date(timestamps[1]),
                  timezone,
                  "zzz"
                )}`}
              </CompoundTag>
            </div>

            {points && (
              <CompoundTag leftContent={"Points:"} size="large">
                {points.toLocaleString()}
              </CompoundTag>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ReaddMissingPointCloudsButton() {
  const lidarMachine = LidarMachineContext.useActorRef();
  const pointCloudDropzone = usePointCloudDropzone(lidarMachine);

  return (
    <Button
      icon="antenna"
      intent="success"
      size="small"
      {...pointCloudDropzone.getRootProps()}
    >
      <input {...pointCloudDropzone.getInputProps()} />
      Re-add Missing Point Clouds
    </Button>
  );
}

function RemoveMissingPointCloudsButton() {
  const lidarMachine = LidarMachineContext.useActorRef();

  const uploadInProgress = useUploadMachineSelector((state) => {
    return state.context.fileUploadActors.length > 0;
  });

  const handleClick = () => {
    lidarMachine.send({ type: "REMOVE_MISSING" });
  };

  return (
    <Button
      icon="trash"
      intent="danger"
      size="small"
      disabled={uploadInProgress}
      onClick={handleClick}
    >
      Remove Missing Point Clouds
    </Button>
  );
}

interface PointCloudListProps {
  pointclouds: ProcessedFile<PointCloudData>[];
  onFocus?: (bbox: number[]) => void;
  fileAgeMap: Map<string, boolean>;
}

export default function PointCloudList(props: PointCloudListProps) {
  const { pointclouds, onFocus } = props;
  const missingPointClouds = pointclouds.filter(
    (pointcloud) =>
      !(pointcloud.file instanceof File) &&
      (pointcloud as any).status !== "uploaded"
  );

  const warningMissingPointClouds = missingPointClouds.length > 0;

  return (
    <div
      css={css`
        flex-grow: 1;
        width: 100%;
        border-top: 1px solid var(--neutral-5);
        display: flex;
        flex-direction: column;
        overflow: hidden;
      `}
    >
      <div
        css={css`
          background-color: var(--neutral-6);
          border-bottom: 1px solid var(--neutral-5);
          padding: 0.5rem 1rem 0.5rem 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <h4
          css={css`
            color: var(--neutral-3);
            margin: 0;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 0.5rem;
          `}
        >
          <span>
            {pointclouds.length === 1
              ? "1 Point Cloud"
              : `${pointclouds.length} Point Clouds`}
          </span>
          {warningMissingPointClouds ? (
            <span
              css={css`
                color: var(--danger);
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 0.25rem;
              `}
            >
              <Icon icon="error" color="var(--danger)" />
              {missingPointClouds.length} Missing
            </span>
          ) : null}
        </h4>
        {warningMissingPointClouds ? (
          <div
            css={css`
              flex-direction: row;
              align-items: center;
              gap: 0.5rem;
              display: flex;
            `}
          >
            <ButtonGroup>
              <ReaddMissingPointCloudsButton />
              <RemoveMissingPointCloudsButton />
            </ButtonGroup>
          </div>
        ) : null}
      </div>
      <div
        css={css`
          flex-grow: 1;
          padding: 1rem;
          overflow: auto;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        `}
      >
        {pointclouds.map((upload) => {
          return (
            <PointCloudRow
              key={upload.id}
              upload={upload}
              onFocus={onFocus}
              isOld={props.fileAgeMap.get(upload.id) ?? false}
            />
          );
        })}
      </div>
      <PointCloudConfirmRemoveDialog />
      <PointCloudConfirmRemoveMissingDialog />
    </div>
  );
}
